import { sleep } from '@/helpers/sleep';
import api from '@/store/api';
import { athena, boro } from '@/store/api/instances';

import type {
  DispenseAssetView,
  RestockRequestView,
  RestockingSummaryView,
} from '@/types/generated/athena';
import type {
  CreateRestockRequest,
  ReconciliationRequest,
  UpdateNoteDispenserRequest,
  UpdateRestockRequest,
} from '@/types/generated/boro';

import type { ApiListingRequest, ApiResponse } from '@bitstopco/bitstop-theme';

export const RestockingApi = api.injectEndpoints({
  endpoints: (build) => ({
    getDispenseAssets: build.query<ApiResponse<DispenseAssetView[]>, void>({
      query: () => athena({ url: 'v2/resources/dispense-assets', method: 'GET' }),
    }),
    getRestockRequests: build.query<ApiResponse<RestockRequestView[]>, ApiListingRequest>({
      query: (data) =>
        athena({
          url: 'v2/AssetRestocking/restock-requests',
          method: 'POST',
          data,
        }),
      transformResponse: (res: ApiResponse<RestockRequestView[]>) => {
        const items = [...res.data];
        const data = items.map((item) => {
          const cassettes = [...(item.cassettes ?? [])].sort(
            (a, b) => (a.cassette_index ?? 0) - (b.cassette_index ?? 0),
          );
          return { ...item, cassettes };
        });
        return { ...res, data };
      },
      providesTags: ['RestockRequests'],
    }),
    getRestockRequest: build.query<ApiResponse<RestockRequestView>, string>({
      query: (id) =>
        athena({
          url: `v2/AssetRestocking/restock-requests/${id}`,
          method: 'GET',
        }),
      transformResponse: (res: ApiResponse<RestockRequestView>) => {
        const cassettes = [...(res.data.cassettes ?? [])].sort(
          (a, b) => (a.cassette_index ?? 0) - (b.cassette_index ?? 0),
        );
        return { ...res, data: { ...res.data, cassettes } };
      },
      providesTags: ['RestockRequest'],
    }),
    getRestockingStats: build.query<ApiResponse<RestockingSummaryView>, void>({
      query: () => athena({ url: 'v2/AssetRestocking/summary', method: 'GET' }),
      providesTags: ['RestockStats'],
    }),
    createRestock: build.mutation<void, CreateRestockRequest>({
      query: (data) => boro({ url: 'v2/restocks', method: 'POST', data }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          await sleep(1000);
          dispatch(api.util.invalidateTags(['RestockRequests', 'RestockRequest', 'RestockStats']));
        } catch {}
      },
    }),
    createReconciliation: build.mutation<void, { id: string; data: ReconciliationRequest }>({
      query: ({ id, data }) =>
        boro({ url: `v2/restocks/${id}/reconciliation`, method: 'POST', data }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          await sleep(1000);
          dispatch(api.util.invalidateTags(['RestockRequests', 'RestockRequest', 'RestockStats']));
        } catch {}
      },
    }),
    editRestock: build.mutation<void, { id: string; data: UpdateRestockRequest }>({
      query: ({ id, data }) => boro({ url: `v2/restocks/${id}`, method: 'PUT', data }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          await sleep(1000);
          dispatch(api.util.invalidateTags(['RestockRequests', 'RestockRequest', 'RestockStats']));
        } catch {}
      },
    }),
    patchNoteDispenser: build.mutation<void, { request: UpdateNoteDispenserRequest; atm: number }>({
      query: ({ atm, request }) =>
        boro({
          url: `v2/Atms/${atm}/note-dispenser`,
          method: 'PATCH',
          data: request,
        }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          await sleep(1000);
          dispatch(api.util.invalidateTags(['Machine']));
        } catch {}
      },
    }),
  }),
});

export const {
  useGetRestockingStatsQuery,
  useGetDispenseAssetsQuery,
  useGetRestockRequestsQuery,
  useCreateRestockMutation,
  useEditRestockMutation,
  useGetRestockRequestQuery,
  usePatchNoteDispenserMutation,
  useCreateReconciliationMutation,
} = RestockingApi;
